import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  RefreshCw, LogOut, CheckCircle, AlertCircle, ShoppingCart, ListChecks,
} from 'lucide-react';
import LoginScreen from './admin/LoginScreen';
import VendasTab from './admin/VendasTab';
import LegacyTab from './admin/LegacyTab';
import { COURSE_LABEL } from './admin/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = 'adminAuth';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('vendas');
  const [message, setMessage] = useState(null);

  // Vendas Eleições 2026
  const [vendas, setVendas] = useState([]);
  const [vendasLoading, setVendasLoading] = useState(false);
  const [vendasProcessing, setVendasProcessing] = useState(new Set());

  // Pré-matrículas legadas
  const [registrations, setRegistrations] = useState([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set());

  // Auto-dismiss messages
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  // ===== Loaders =====
  const loadVendas = useCallback(async () => {
    setVendasLoading(true);
    try {
      const res = await axios.get(`${API}/vendas-eleicoes`);
      setVendas(res.data.vendas || []);
    } catch (err) {
      console.error('Erro ao carregar vendas:', err);
      setMessage({ type: 'error', text: 'Erro ao carregar vendas Eleições 2026' });
    } finally {
      setVendasLoading(false);
    }
  }, []);

  const loadRegistrations = useCallback(async () => {
    setLegacyLoading(true);
    try {
      const response = await axios.get(`${API}/pre-matriculas`);
      setRegistrations(response.data.registrations);
    } catch (error) {
      console.error('Erro ao carregar pré-matrículas:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar pré-matrículas legadas' });
    } finally {
      setLegacyLoading(false);
    }
  }, []);

  // Verifica sessão ao montar (valida JWT contra backend)
  useEffect(() => {
    const token = sessionStorage.getItem(AUTH_KEY);
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/admin/verify-token`, null, { params: { token } });
        if (cancelled) return;
        if (res.data?.valid) {
          setIsAuthenticated(true);
          loadVendas();
          loadRegistrations();
        } else {
          sessionStorage.removeItem(AUTH_KEY);
        }
      } catch (err) {
        console.error('Erro ao validar sessão:', err);
        if (!cancelled) sessionStorage.removeItem(AUTH_KEY);
      }
    })();
    return () => { cancelled = true; };
  }, [loadVendas, loadRegistrations]);

  // ===== Auth =====
  const handleLogin = useCallback(async (loginData) => {
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      if (response.data.success) {
        sessionStorage.setItem(AUTH_KEY, response.data.token);
        setIsAuthenticated(true);
        loadVendas();
        loadRegistrations();
        return null;
      }
      return response.data.message || 'Usuário ou senha incorretos';
    } catch (err) {
      console.error('Erro no login:', err);
      if (err.response?.status === 429) {
        return err.response.data?.detail || 'Muitas tentativas falhas. Aguarde antes de tentar novamente.';
      }
      return 'Erro ao conectar com o servidor. Tente novamente.';
    }
  }, [loadVendas, loadRegistrations]);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(AUTH_KEY);
    setIsAuthenticated(false);
    setRegistrations([]);
    setVendas([]);
  }, []);

  // ===== Helpers para tracking de processamento =====
  const markProcessing = useCallback((setter, id) => {
    setter((prev) => new Set([...prev, id]));
  }, []);
  const unmarkProcessing = useCallback((setter, id) => {
    setter((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }, []);

  // ===== Ações Vendas =====
  const handleCreateVenda = useCallback(async (payload) => {
    try {
      await axios.post(`${API}/vendas-eleicoes`, payload);
      setMessage({
        type: 'success',
        text: `Pré-matrícula registrada: ${payload.name} (${COURSE_LABEL[payload.course]})`,
      });
      loadVendas();
      return true;
    } catch (err) {
      console.error('Erro ao criar venda:', err);
      setMessage({ type: 'error', text: 'Erro ao registrar pré-matrícula' });
      return false;
    }
  }, [loadVendas]);

  const handleConfirmVendaPayment = useCallback(async (id, name) => {
    markProcessing(setVendasProcessing, id);
    try {
      await axios.post(`${API}/vendas-eleicoes/${id}/confirm-payment`);
      setMessage({ type: 'success', text: `Pagamento confirmado: ${name}` });
      loadVendas();
    } catch (err) {
      console.error('Erro ao confirmar pagamento:', err);
      setMessage({ type: 'error', text: `Erro ao confirmar pagamento de ${name}` });
    } finally {
      unmarkProcessing(setVendasProcessing, id);
    }
  }, [loadVendas, markProcessing, unmarkProcessing]);

  const handleUpdateVendaStatus = useCallback(async (id, name, status) => {
    markProcessing(setVendasProcessing, id);
    try {
      await axios.put(`${API}/vendas-eleicoes/${id}`, { status });
      setMessage({ type: 'success', text: `${name}: status atualizado para ${status}` });
      loadVendas();
    } catch (err) {
      console.error('Erro ao atualizar venda:', err);
      setMessage({ type: 'error', text: `Erro ao atualizar ${name}` });
    } finally {
      unmarkProcessing(setVendasProcessing, id);
    }
  }, [loadVendas, markProcessing, unmarkProcessing]);

  const handleDeleteVenda = useCallback(async (id, name) => {
    if (!window.confirm(`Tem certeza que deseja excluir a pré-matrícula de ${name}? Esta ação não pode ser desfeita.`)) return;
    markProcessing(setVendasProcessing, id);
    try {
      await axios.delete(`${API}/vendas-eleicoes/${id}`);
      setMessage({ type: 'success', text: `${name} foi removido` });
      loadVendas();
    } catch (err) {
      console.error('Erro ao excluir venda:', err);
      setMessage({ type: 'error', text: `Erro ao excluir ${name}` });
    } finally {
      unmarkProcessing(setVendasProcessing, id);
    }
  }, [loadVendas, markProcessing, unmarkProcessing]);

  // ===== Ações Pré-matrículas legadas =====
  const handleConfirmLegacyPayment = useCallback(async (matriculaId, studentName) => {
    markProcessing(setProcessingIds, matriculaId);
    try {
      await axios.post(`${API}/admin/matricula/${matriculaId}/confirm-payment`);
      setMessage({ type: 'success', text: `Pagamento confirmado para ${studentName}` });
      loadRegistrations();
    } catch (err) {
      console.error('Erro ao confirmar pagamento (legacy):', err);
      setMessage({ type: 'error', text: `Erro ao confirmar pagamento para ${studentName}` });
    } finally {
      unmarkProcessing(setProcessingIds, matriculaId);
    }
  }, [loadRegistrations, markProcessing, unmarkProcessing]);

  const handleConfirmLegacyMatricula = useCallback(async (matriculaId, studentName) => {
    markProcessing(setProcessingIds, matriculaId);
    try {
      await axios.put(`${API}/admin/matricula/${matriculaId}`, { status: 'confirmed', payment_status: 'pending' });
      setMessage({ type: 'success', text: `Matrícula confirmada para ${studentName}` });
      loadRegistrations();
    } catch (err) {
      console.error('Erro ao confirmar matrícula (legacy):', err);
      setMessage({ type: 'error', text: `Erro ao confirmar matrícula para ${studentName}` });
    } finally {
      unmarkProcessing(setProcessingIds, matriculaId);
    }
  }, [loadRegistrations, markProcessing, unmarkProcessing]);

  const handleDeleteLegacyStudent = useCallback(async (matriculaId, studentName) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${studentName}?\n\nEsta ação não pode ser desfeita.`)) return;
    markProcessing(setProcessingIds, matriculaId);
    try {
      await axios.delete(`${API}/admin/matricula/${matriculaId}`);
      setMessage({ type: 'success', text: `${studentName} foi removido com sucesso` });
      loadRegistrations();
    } catch (err) {
      console.error('Erro ao excluir aluno (legacy):', err);
      setMessage({ type: 'error', text: `Erro ao excluir ${studentName}` });
    } finally {
      unmarkProcessing(setProcessingIds, matriculaId);
    }
  }, [loadRegistrations, markProcessing, unmarkProcessing]);

  const handleRefresh = useCallback(() => {
    loadVendas();
    loadRegistrations();
  }, [loadVendas, loadRegistrations]);

  if (!isAuthenticated) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Painel Administrativo</h1>
              <p className="text-gray-600 text-sm">PoliTalks · Gestão de Inscrições</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleRefresh} variant="outline" size="sm" disabled={vendasLoading || legacyLoading} data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${vendasLoading || legacyLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </div>
          </div>

          <div className="flex gap-2 mt-4 border-b">
            <button
              data-testid="tab-vendas"
              onClick={() => setActiveTab('vendas')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${activeTab === 'vendas' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <ShoppingCart className="w-4 h-4 inline-block mr-1.5" /> Compras Eleições 2026 ({vendas.length})
            </button>
            <button
              data-testid="tab-legacy"
              onClick={() => setActiveTab('legacy')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${activeTab === 'legacy' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <ListChecks className="w-4 h-4 inline-block mr-1.5" /> Pré-Matrículas Legadas ({registrations.length})
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {message && (
          <div data-testid="admin-message" className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <div className="flex items-center">
              {message.type === 'success' ? <CheckCircle className="w-5 h-5 mr-2" /> : <AlertCircle className="w-5 h-5 mr-2" />}
              {message.text}
            </div>
          </div>
        )}

        {activeTab === 'vendas' ? (
          <VendasTab
            vendas={vendas}
            vendasLoading={vendasLoading}
            vendasProcessing={vendasProcessing}
            onCreate={handleCreateVenda}
            onConfirmPayment={handleConfirmVendaPayment}
            onUpdateStatus={handleUpdateVendaStatus}
            onDelete={handleDeleteVenda}
          />
        ) : (
          <LegacyTab
            registrations={registrations}
            legacyLoading={legacyLoading}
            processingIds={processingIds}
            onConfirmPayment={handleConfirmLegacyPayment}
            onConfirmMatricula={handleConfirmLegacyMatricula}
            onDelete={handleDeleteLegacyStudent}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
